import { useEffect, useMemo } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { Mail, MessageCircle, Clock, AlertCircle, ShieldCheck } from 'lucide-react';
import { SEO } from '../components/SEO';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';

const SITE_URL = 'https://bolaoelite.com.br';
const CONTACT_EMAIL = 'suporte@bolaoelite.com.br';

export default function Contato() {
  const { t } = useTranslation('institutional');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      url: `${SITE_URL}/contato`,
      name: 'Contato — Bolão Elite',
      description:
        'Canais oficiais para contato com o Bolão Elite: suporte, sugestões, parcerias e imprensa.',
      inLanguage: 'pt-BR',
      mainEntity: {
        '@type': 'Organization',
        name: 'Bolão Elite',
        url: SITE_URL,
        contactPoint: [
          {
            '@type': 'ContactPoint',
            email: CONTACT_EMAIL,
            contactType: 'customer support',
            availableLanguage: ['Portuguese'],
            areaServed: 'BR',
          },
        ],
      },
    }),
    []
  );

  const seo = useLocalizedSeo('contato');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO
        title={t('contact.seo_title', 'Contato — Bolão Elite')}
        description={t('contact.seo_desc', 'Fale com a equipe do Bolão Elite. Suporte ao usuário, sugestões, parcerias e contato de imprensa. Resposta em até 2 dias úteis.')}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        locale={seo.locale}
        alternates={seo.alternates}
        keywords="contato bolão elite, suporte, atendimento, fale conosco, parceria"
        jsonLd={jsonLd}
      />

      <PublicHeader />

      <section className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <nav className="text-sm text-white/70 mb-3" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{t('common:nav.home', 'Início')}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{t('contact.breadcrumb', 'Contato')}</span>
          </nav>
          <span className="inline-block text-xs font-semibold text-primary-200 uppercase tracking-widest">
            {t('contact.eyebrow', 'Fale com a gente')}
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            {t('contact.h1', 'Contato — Bolão Elite')}
          </h1>
          <p className="mt-4 text-base text-white/80 leading-relaxed max-w-2xl">
            {t('contact.intro', 'Estamos sempre prontos para ouvir você. Tem uma dúvida sobre como usar o bolão? Encontrou um bug? Quer sugerir uma melhoria, falar sobre parceria ou só dar um oi? Use o canal abaixo que melhor se encaixa.')}
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-10">

        <section className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Contato%20pelo%20site`}
            className="group block bg-white border border-gray-200 rounded-2xl p-6 hover:border-primary-300 hover:shadow-md transition-all"
          >
            <div className="w-11 h-11 bg-primary-100 text-primary-700 rounded-xl flex items-center justify-center mb-4">
              <Mail className="w-5 h-5" />
            </div>
            <h2 className="text-base font-bold text-gray-900">{t('contact.email_title', 'E-mail oficial')}</h2>
            <p className="mt-1 text-sm text-primary-700 font-semibold group-hover:underline break-all">
              {CONTACT_EMAIL}
            </p>
            <p className="mt-3 text-sm text-gray-500 leading-relaxed">
              {t('contact.email_desc', 'Canal principal para suporte, sugestões, parcerias e imprensa. Respondemos em até 2 dias úteis.')}
            </p>
          </a>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="w-11 h-11 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center mb-4">
              <MessageCircle className="w-5 h-5" />
            </div>
            <h2 className="text-base font-bold text-gray-900">{t('contact.report_title', 'Reportar Bug ou Fale Conosco')}</h2>
            <p className="mt-3 text-sm text-gray-500 leading-relaxed">
              <Trans i18nKey="contact.report_desc" t={t}>
                Já tem conta? Use o atalho <strong>&quot;Reportar Bug ou Fale Conosco&quot;</strong> no rodapé da plataforma (site ou app). É o jeito mais rápido — a gente já identifica seu usuário automaticamente.
              </Trans>
            </p>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t('contact.expect_title', 'O que esperar do nosso atendimento')}
          </h2>
          <div className="space-y-4">
            <Bullet icon={<Clock className="w-5 h-5" />} title={t('contact.expect_1_title', 'Resposta em até 2 dias úteis')}>
              {t('contact.expect_1_text', 'Lemos cada mensagem. Para erros críticos (não consegue acessar a conta, cobrança indevida), a prioridade é máxima.')}
            </Bullet>
            <Bullet icon={<ShieldCheck className="w-5 h-5" />} title={t('contact.expect_2_title', 'Sua privacidade respeitada')}>
              {t('contact.expect_2_text', 'Sua mensagem é tratada com confidencialidade. Não compartilhamos e-mails com terceiros nem usamos pra disparo de marketing sem seu consentimento explícito.')}
            </Bullet>
            <Bullet icon={<AlertCircle className="w-5 h-5" />} title={t('contact.expect_3_title', 'O que não respondemos')}>
              <Trans i18nKey="contact.expect_3_text" t={t}>
                Não damos palpites de jogos, não indicamos casas de apostas e não somos casa de aposta. Bolão Elite é uma plataforma de bolão entre amigos, sem jogo a dinheiro — entenda mais nos <Link to="/termos-de-uso" className="text-primary-600 font-semibold hover:underline">Termos de Uso</Link>.
              </Trans>
            </Bullet>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t('contact.faq_title', 'Perguntas frequentes antes de escrever')}
          </h2>
          <div className="space-y-3">
            <Faq question={t('contact.faq_1_q', 'Como criar um bolão?')}>
              <Trans i18nKey="contact.faq_1_a" t={t}>
                Faça login, vá em &quot;Meus bolões&quot; e toque em &quot;Criar bolão&quot;. Em menos de um minuto você tem um bolão pronto pra receber palpites. Detalhes em <Link to="/como-jogar" className="text-primary-600 font-semibold hover:underline">Como jogar</Link>.
              </Trans>
            </Faq>
            <Faq question={t('contact.faq_2_q', 'Esqueci minha senha. O que faço?')}>
              {t('contact.faq_2_a', 'Use o link "Esqueci minha senha" na tela de login. Você vai receber um e-mail com instruções para criar uma nova senha em poucos minutos.')}
            </Faq>
            <Faq question={t('contact.faq_3_q', 'Como cancelar o Premium?')}>
              <Trans i18nKey="contact.faq_3_a" t={t}>
                O cancelamento é feito direto pelo app ou site, em <em>Minha conta &rarr; Assinatura &rarr; Cancelar</em>, sem precisar entrar em contato. Você mantém os benefícios Premium até o fim do ciclo já pago.
              </Trans>
            </Faq>
            <Faq question={t('contact.faq_4_q', 'Vocês têm app para iPhone?')}>
              {t('contact.faq_4_a', 'Hoje o app oficial está disponível para Android (Google Play). Para iPhone, recomendamos usar o site no Safari — ele funciona como Progressive Web App (PWA) e pode ser adicionado à tela inicial.')}
            </Faq>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

function Bullet({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4">
      <div className="w-9 h-9 bg-primary-50 text-primary-700 rounded-lg flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-600 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function Faq({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <details className="group bg-white border border-gray-200 rounded-xl">
      <summary className="cursor-pointer p-4 font-semibold text-sm text-gray-900 flex items-center justify-between list-none">
        <span>{question}</span>
        <span className="text-gray-400 group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">
        {children}
      </div>
    </details>
  );
}
